import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "@hooks/useDarkMode";

export default function EsgResourceEfficiencyChart({
  months = [],
  completedVolume = [],
  baselineVolume = [],
  waterSaved = [],
  loading = false,
  selectedYear = new Date().getFullYear(),
}) {
  const { t } = useTranslation();
  const { isDarkMode } = useDarkMode();

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthIndex = today.getMonth();
  const isCurrentYear = selectedYear === currentYear;

  const visibleData = useMemo(() => {
    const visibleLength = isCurrentYear
      ? Math.min(currentMonthIndex + 1, months.length)
      : months.length;

    return {
      months: months.slice(0, visibleLength),
      completedVolume: completedVolume.slice(0, visibleLength),
      baselineVolume: baselineVolume.slice(0, visibleLength),
      waterSaved: waterSaved.slice(0, visibleLength),
    };
  }, [months, completedVolume, baselineVolume, waterSaved, isCurrentYear, currentMonthIndex]);

  const {
    months: visibleMonths,
    completedVolume: visibleCompletedVolume,
    baselineVolume: visibleBaselineVolume,
    waterSaved: visibleWaterSaved,
  } = visibleData;

  const option = useMemo(() => {
    if (!visibleMonths.length) return {};

    const textColor = isDarkMode ? "#A1B5C8" : "#6b7280";
    const axisLineColor = isDarkMode ? "#162535" : "#e5e7eb";
    const splitLineColor = isDarkMode ? "#162535" : "#f3f4f6";

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        backgroundColor: isDarkMode ? "#0F1E2C" : "#fff",
        borderColor: isDarkMode ? "#162535" : "#e5e7eb",
        textStyle: { color: isDarkMode ? "#D4E4EE" : "#131314" },
      },
      legend: {
        data: [t("esg.completed"), t("esg.baseline"), t("esg.water_saved")],
        bottom: 0,
        itemGap: 24,
        textStyle: { fontSize: 12, color: textColor },
      },
      grid: {
        top: 16,
        right: 16,
        bottom: 48,
        left: 48,
        containLabel: false,
      },
      xAxis: {
        type: "category",
        data: visibleMonths,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisTick: { show: false },
        axisLabel: { color: textColor, fontSize: 11 },
      },
      yAxis: {
        type: "value",
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: splitLineColor } },
        axisLabel: {
          color: textColor,
          fontSize: 11,
          formatter: (val) => {
            if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
            return val;
          },
        },
      },
      series: [
        {
          name: t("esg.completed"),
          type: "bar",
          data: visibleCompletedVolume,
          barMaxWidth: 28,
          itemStyle: {
            color: "#307F92",
            borderRadius: [4, 4, 0, 0],
          },
        },
        {
          name: t("esg.baseline"),
          type: "line",
          data: visibleBaselineVolume,
          smooth: true,
          symbol: "none",
          lineStyle: {
            color: isDarkMode ? "#73879D" : "#9ca3af",
            type: "dashed",
            width: 2,
          },
          itemStyle: { color: isDarkMode ? "#73879D" : "#9ca3af" },
        },
        {
          name: t("esg.water_saved"),
          type: "line",
          data: visibleWaterSaved,
          smooth: true,
          symbol: "none",
          lineStyle: { color: "#7BB32E", width: 2 },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(123, 179, 46, 0.25)" },
                { offset: 1, color: "rgba(123, 179, 46, 0.02)" },
              ],
            },
          },
          itemStyle: { color: "#7BB32E" },
        },
      ],
    };
  }, [
    visibleMonths,
    visibleCompletedVolume,
    visibleBaselineVolume,
    visibleWaterSaved,
    t,
    isDarkMode,
  ]);

  if (!months.length && !loading) {
    return (
      <div
        style={{
          height: 350,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: isDarkMode ? "#73879D" : "#9ca3af",
        }}
      >
        {t("dashboard.no_data")}
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: 350, width: "100%" }}
      showLoading={loading}
      notMerge
    />
  );
}
